import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { getApiUrl } from '../client/lib/config';
import { Button } from '../client/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../client/components/ui/card';
import { AdvancedPDFViewer } from './AdvancedPDFViewer';
import { ResizablePanels } from './ResizablePanels';
import './StageC.css';

interface ContextEntry {
    term: string;
    page: string; // Changed from number to string to support non-numeric pages like "2n54"
    context: string;
    context_condensed?: string | null;
    parent_term?: string | null;
    see_also?: string[] | string; // Allow both array and string from CSV
}

interface ContextReviewData {
    termsWithContext: ContextEntry[];
    uniqueTerms: string[];
    workflowId?: string;
}

interface ContextEditDecision {
    term: string;
    page: string; // Changed from number to string to support non-numeric pages like "2n54"
    original_term: string;
    edited_term: string;
    original_context_condensed: string | null;
    edited_context_condensed: string | null;
    original_page: string; // Changed from number to string
    edited_page: string; // Allow string to handle footnote references
}

interface StageDProps {
    reviewData: ContextReviewData;
    projectId: string;
    filename: string;
    onSubmitReview: (decisions: ContextEditDecision[]) => void;
    isSubmitting?: boolean;
    pageRange?: { startPage?: number; endPage?: number; endnotesEnabled?: boolean; endnotesStartPage?: number }; // CRITICAL: Page range for endnotes logic
}

export const StageD: React.FC<StageDProps> = ({
    reviewData,
    projectId,
    filename,
    onSubmitReview,
    isSubmitting = false,
    pageRange
}) => {
    // Debug: Check what data we're receiving
    console.log('[StageD] Component loaded with data:', {
        termsWithContextCount: reviewData.termsWithContext?.length || 0,
        uniqueTermsCount: reviewData.uniqueTerms?.length || 0,
        workflowId: reviewData.workflowId
    });

    // Field tracking system - flexible for future expansion
    const [fieldUpdates, setFieldUpdates] = useState<Record<string, Record<string, string>>>({
        term: {},      // For term edits
        page: {},      // For page edits  
        context: {}    // For context edits
    });
    
    // State for ALL completed Stage C decisions integration (C1+C2+C3)
    const [allDecisions, setAllDecisions] = useState<any[]>([]);
    const [allCrossReferences, setAllCrossReferences] = useState<{ [term: string]: string[] }>({});
    const [allSubentries, setAllSubentries] = useState<{ [term: string]: string }>({});
    const [allMergedTerms, setAllMergedTerms] = useState<{ [term: string]: string }>({});
    const [isMergedExpanded, setIsMergedExpanded] = useState<boolean>(false);
    const [isSubentriesExpanded, setIsSubentriesExpanded] = useState<boolean>(false);
    const [isCrossRefsExpanded, setIsCrossRefsExpanded] = useState<boolean>(false);
    
    // Pagination states
    const [mergedPage, setMergedPage] = useState<number>(1);
    const [subentriesPage, setSubentriesPage] = useState<number>(1);
    const [crossRefsPage, setCrossRefsPage] = useState<number>(1);
    
    const ITEMS_PER_PAGE = 10;

    // Pagination helpers
    const getPaginatedItems = (items: any[], page: number) => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return items.slice(startIndex, endIndex);
    };

    const getTotalPages = (itemCount: number) => Math.ceil(itemCount / ITEMS_PER_PAGE);

    const renderPaginationControls = (currentPage: number, totalPages: number, onPageChange: (page: number) => void) => {
        if (totalPages <= 1) return null;
        
        return (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                <span className="text-xs text-muted-foreground">
                    Page {currentPage} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-2 py-1 text-xs bg-muted/20 border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/30 text-foreground"
                    >
                        Previous
                    </button>
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-2 py-1 text-xs bg-muted/20 border border-border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted/30 text-foreground"
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    };
    
    const [highlightedTerm, setHighlightedTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isPdfMinimized, setIsPdfMinimized] = useState(false);
    const [currentTablePage, setCurrentTablePage] = useState(1);
    const [isResizing, setIsResizing] = useState(false);
    
    // Tooltip state for better hover functionality
    const [tooltip, setTooltip] = useState<{
        show: boolean;
        content: string;
        x: number;
        y: number;
        type: 'context' | 'crossref';
    }>({
        show: false,
        content: '',
        x: 0,
        y: 0,
        type: 'context'
    });
    
    // Auto-save state using refs to prevent re-renders
    const isSavingRef = useRef<boolean>(false);
    const lastSavedDataRef = useRef<string>('');
    const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const scrollPositionRef = useRef<number>(0);
    
    // UI state for save indicators
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
    
    // Column widths (resizable)
    const [termColumnWidth, setTermColumnWidth] = useState(200);
    const [pageColumnWidth, setPageColumnWidth] = useState(80);
    const [condensedContextColumnWidth, setCondensedContextColumnWidth] = useState(250);
    const [fullContextColumnWidth, setFullContextColumnWidth] = useState(300);
    const [relationshipColumnWidth, setRelationshipColumnWidth] = useState(200);
    
    const itemsPerPage = 50;

    // Helper to format page with 'n' suffix for endnotes (apply same logic as earlier stages)
    const formatPageDisplay = useCallback((page: string) => {
        // If page already carries an 'n' suffix, keep as-is
        if (/\dn\d+/i.test(page) || /n\d+/i.test(page) || /\d+n$/i.test(page)) {
            return page;
        }
        
        // Apply endnote suffix logic if pageRange is configured
        if (pageRange?.endnotesEnabled && typeof pageRange.endnotesStartPage === 'number') {
            const pageNum = parseInt(page, 10);
            if (!isNaN(pageNum)) {
                const startPage = pageRange.startPage || 1;
                const pdfPageNum = pageNum + (startPage - 1);
                if (pdfPageNum >= pageRange.endnotesStartPage) {
                    console.log(`[StageD] ✅ ADDING 'n' SUFFIX: ${pageNum}n (PDF page ${pdfPageNum} >= endnotes start ${pageRange.endnotesStartPage})`);
                    return `${pageNum}n`;
                } else {
                    console.log(`[StageD] ❌ NO 'n' SUFFIX: PDF page ${pdfPageNum} < endnotes start ${pageRange.endnotesStartPage}`);
                }
            }
        } else {
            console.log(`[StageD] ❌ ENDNOTES NOT ENABLED or missing endnotesStartPage`);
        }
        
        return page;
    }, [pageRange]);

    // Backoff + absolute URL request helper for Stage D
    const requestWithBackoff = useCallback(async (url: string, options: RequestInit = {}, maxAttempts: number = 6): Promise<Response> => {
        const token = localStorage.getItem('orca_auth_token');
        if (!token) throw new Error('No authentication token found');
        const absUrl = getApiUrl(url);
        let delay = 250;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const resp = await fetch(absUrl, {
                    ...options,
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        ...options.headers
                    }
                });
                return resp;
            } catch (err: any) {
                if (attempt === maxAttempts) throw err;
                await new Promise(r => setTimeout(r, delay));
                delay = Math.min(8000, Math.round(delay * 1.8));
            }
        }
        throw new Error('Backoff exhausted');
    }, []);

    // Generic field update function - easily expandable for new columns
    const updateField = useCallback((fieldType: string, term: string, page: string, newValue: string) => {
        const key = `${term}:${page}`;
        
        console.log(`[StageD] 🔄 updateField called:`, { fieldType, term, page, newValue, key });
        
        setFieldUpdates(prev => {
            const updated = {
                ...prev,
                [fieldType]: {
                    ...prev[fieldType],
                    [key]: newValue
                }
            };
            console.log(`[StageD] 📝 fieldUpdates updated:`, updated);
            return updated;
        });
        
        console.log(`[StageD] ✅ ${fieldType} field updated: "${term}" on page ${page} -> "${newValue}"`);
    }, []);

    // Fetch ALL completed decisions (C1, C2, C3) to integrate into Stage D display
    useEffect(() => {
        const fetchCompletedDecisions = async () => {
            try {
                const response = await requestWithBackoff(`/api/projects/${projectId}/completed-relationships`);
                
                if (response.ok) {
                    const data = await response.json();
                    setAllDecisions([]); // Relationships endpoint doesn't return raw decisions
                    
                    // Combine ALL cross-references with existing see_also relationships from the data
                    const existingCrossRefs: { [term: string]: string[] } = {};
                    reviewData.termsWithContext?.forEach(entry => {
                        if (entry.see_also) {
                            const seeAlsoArray = Array.isArray(entry.see_also) ? entry.see_also : [entry.see_also];
                            const filteredSeeAlso = seeAlsoArray.filter(ref => ref && ref.trim() !== '');
                            if (filteredSeeAlso.length > 0) {
                                existingCrossRefs[entry.term] = filteredSeeAlso;
                            }
                        }
                    });
                    
                    // Merge ALL cross-references with existing ones (Stage C takes precedence for conflicts)
                    const combinedCrossRefs = { ...existingCrossRefs, ...(data.crossReferences || {}) };
                    setAllCrossReferences(combinedCrossRefs);
                    
                    // Combine ALL subentries with existing parent_term relationships from the data
                    const existingSubentries: { [term: string]: string } = {};
                    reviewData.termsWithContext?.forEach(entry => {
                        if (entry.parent_term && entry.parent_term.trim() !== '') {
                            existingSubentries[entry.term] = entry.parent_term;
                        }
                    });
                    
                    // Merge ALL subentries with existing ones (Stage C takes precedence for conflicts)
                    const combinedSubentries = { ...existingSubentries, ...(data.subentries || {}) };
                    setAllSubentries(combinedSubentries);
                    
                    setAllMergedTerms(data.mergedTerms || {});
                    console.log(`[StageD] Loaded ALL completed relationships (C1+C2+C3) for display:`, {
                        crossReferences: Object.keys(combinedCrossRefs).length,
                        subentries: Object.keys(combinedSubentries).length,
                        mergedTerms: Object.keys(data.mergedTerms || {}).length,
                        existingCrossRefs: Object.keys(existingCrossRefs).length,
                        existingSubentries: Object.keys(existingSubentries).length
                    });
                } else {
                    console.warn('[StageD] Failed to fetch completed relationships:', response.statusText);
                }
            } catch (error) {
                console.warn('[StageD] Error fetching completed relationships:', error);
            }
        };

        if (projectId) {
            fetchCompletedDecisions();
        }
    }, [projectId]);

    // Auto-save function with performance optimizations
    const autoSave = useCallback(async () => {
        console.log(`[StageD] 🚀 autoSave called - projectId: ${projectId}, isSaving: ${isSavingRef.current}`);
        
        if (!projectId || isSavingRef.current) {
            console.log(`[StageD] ❌ autoSave aborted - projectId: ${!!projectId}, isSaving: ${isSavingRef.current}`);
            return;
        }

        // Check if data has actually changed since last save
        const currentDataString = JSON.stringify(fieldUpdates);
        console.log(`[StageD] 📊 Data comparison:`, {
            currentLength: currentDataString.length,
            lastSavedLength: lastSavedDataRef.current.length,
            hasChanged: currentDataString !== lastSavedDataRef.current
        });
        
        if (currentDataString === lastSavedDataRef.current) {
            console.log(`[StageD] ⏭️ No changes detected, skipping save`);
            return; // No changes, skip save
        }

        // Save current scroll position
        const container = document.querySelector('.scroll-smooth, .overflow-auto');
        if (container) {
            scrollPositionRef.current = container.scrollTop;
        }

        // Cancel any pending request
        if (abortControllerRef.current) {
            console.log(`[StageD] 🛑 Aborting previous request`);
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();
        isSavingRef.current = true;
        setIsSaving(true); // Update UI state
        
        console.log(`[StageD] 💾 Starting auto-save process...`);

        try {
            const decisions: ContextEditDecision[] = [];
            
            // Collect all changes across all field types
            const allKeys = new Set<string>();
            Object.values(fieldUpdates).forEach(fieldData => {
                Object.keys(fieldData).forEach(key => allKeys.add(key));
            });
            
            console.log(`[StageD] 🔍 Processing ${allKeys.size} unique field updates:`, Array.from(allKeys));
            
            allKeys.forEach(key => {
                const [term, pageStr] = key.split(':');
                // Keep page as string to handle non-numeric values like "2n54"
                const originalEntry = reviewData.termsWithContext.find(
                    entry => entry.term === term && entry.page.toString() === pageStr
                );
                
                console.log(`[StageD] 🔄 Processing key "${key}":`, {
                    term, page: pageStr, foundOriginal: !!originalEntry
                });
                
                if (originalEntry) {
                    const editedTerm = fieldUpdates.term[key];
                    const editedContext = fieldUpdates.context[key];
                    const editedPage = fieldUpdates.page[key];
                    
                    console.log(`[StageD] 📋 RAW FIELD VALUES for "${key}":`, {
                        'fieldUpdates.term[key]': editedTerm,
                        'fieldUpdates.context[key]': editedContext,
                        'fieldUpdates.page[key]': editedPage,
                        'originalEntry.term': originalEntry.term,
                        'originalEntry.context_condensed': originalEntry.context_condensed,
                        'originalEntry.page': originalEntry.page
                    });
                    
                    console.log(`[StageD] 📋 DETAILED Field values for "${key}":`, {
                        editedTerm, editedContext, editedPage,
                        originalTerm: originalEntry.term,
                        originalContext: originalEntry.context_condensed,
                        originalPage: originalEntry.page
                    });
                    
                    // Only create decisions for actual changes
                    const hasTermChange = editedTerm !== undefined && editedTerm !== originalEntry.term;
                    const hasContextChange = editedContext !== undefined && editedContext !== (originalEntry.context_condensed || '');
                    const hasPageChange = editedPage !== undefined && editedPage !== originalEntry.page.toString();
                    
                    console.log(`[StageD] 🔍 SUPER DETAILED Change detection for "${key}":`, {
                        hasTermChange, hasContextChange, hasPageChange,
                        termComparison: {
                            editedTerm,
                            originalTerm: originalEntry.term,
                            isUndefined: editedTerm === undefined,
                            isEqual: editedTerm === originalEntry.term,
                            bothValues: `"${editedTerm}" vs "${originalEntry.term}"`,
                            typeCheck: `${typeof editedTerm} vs ${typeof originalEntry.term}`
                        },
                        contextComparison: {
                            editedContext,
                            originalContext: originalEntry.context_condensed || '',
                            isUndefined: editedContext === undefined,
                            isEqual: editedContext === (originalEntry.context_condensed || ''),
                            bothValues: `"${editedContext}" vs "${originalEntry.context_condensed || ''}"`,
                            typeCheck: `${typeof editedContext} vs ${typeof (originalEntry.context_condensed || '')}`
                        },
                        pageComparison: {
                            editedPage,
                            originalPage: originalEntry.page.toString(),
                            isUndefined: editedPage === undefined,
                            isEqual: editedPage === originalEntry.page.toString(),
                            bothValues: `"${editedPage}" vs "${originalEntry.page.toString()}"`,
                            typeCheck: `${typeof editedPage} vs ${typeof originalEntry.page.toString()}`
                        }
                    });
                    
                    if (hasTermChange || hasContextChange || hasPageChange) {
                        const decision = {
                            term,
                            page: pageStr,
                            original_term: originalEntry.term,
                            edited_term: editedTerm !== undefined ? editedTerm : originalEntry.term,
                            original_context_condensed: originalEntry.context_condensed || null,
                            edited_context_condensed: editedContext !== undefined ? editedContext : (originalEntry.context_condensed || null),
                            original_page: originalEntry.page,
                            edited_page: editedPage !== undefined ? editedPage : originalEntry.page.toString()
                        };
                        decisions.push(decision);
                        console.log(`[StageD] ✅ DECISION CREATED for "${key}":`, decision);
                    } else {
                        console.log(`[StageD] ❌ NO CHANGES DETECTED for "${key}" - all comparison flags are false`);
                    }
                } else {
                    console.log(`[StageD] ❌ NO ORIGINAL ENTRY FOUND for "${key}" - cannot create decision`);
                }
            });

            console.log(`[StageD] 📤 Sending ${decisions.length} decisions to server:`, decisions);

            if (decisions.length > 0) {
                const requestBody = {
                    decisions: decisions,
                    fieldUpdates: fieldUpdates
                };
                
                console.log(`[StageD] 🌐 Making API request to /api/projects/${projectId}/save-stage-d-edits`);
                console.log(`[StageD] 📦 Request body:`, requestBody);
                
                // Use dedicated auto-save API endpoint like other stages
                const response = await requestWithBackoff(`/api/projects/${projectId}/save-stage-d-edits`, {
                    method: 'POST',
                    body: JSON.stringify(requestBody),
                    signal: abortControllerRef.current.signal
                });

                console.log(`[StageD] 📡 API response status: ${response.status}`);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`[StageD] ❌ Auto-save failed - Status: ${response.status}, Error: ${errorText}`);
                    throw new Error(`Auto-save failed: ${response.status} - ${errorText}`);
                }
                
                const responseData = await response.json();
                console.log(`[StageD] ✅ Auto-save response:`, responseData);
                
                // Update last saved state and UI
                lastSavedDataRef.current = currentDataString;
                setLastSaveTime(new Date());
                
                console.log(`[StageD] 🎉 Auto-save completed successfully`);
            } else {
                console.log(`[StageD] ℹ️ No actual changes to save`);
            }
            
            // Restore scroll position after a brief delay
            setTimeout(() => {
                const container = document.querySelector('.scroll-smooth, .overflow-auto');
                if (container && scrollPositionRef.current > 0) {
                    container.scrollTop = scrollPositionRef.current;
                }
            }, 50);
            
        } catch (error) {
            // Don't log aborted requests as errors
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error(`[StageD] ❌ Auto-save failed:`, error);
            } else {
                console.log(`[StageD] 🛑 Auto-save aborted`);
            }
        } finally {
            isSavingRef.current = false;
            setIsSaving(false); // Update UI state
            abortControllerRef.current = null;
            console.log(`[StageD] 🏁 Auto-save process finished`);
        }
    }, [projectId, fieldUpdates, reviewData.termsWithContext]);

    // Debounced auto-save scheduler
    const scheduleAutoSave = useCallback(() => {
        console.log(`[StageD] ⏰ scheduleAutoSave called - projectId: ${!!projectId}, isSaving: ${isSavingRef.current}`);
        
        if (!projectId || isSavingRef.current) {
            console.log(`[StageD] ❌ scheduleAutoSave aborted - projectId: ${!!projectId}, isSaving: ${isSavingRef.current}`);
            return;
        }

        if (autoSaveTimeoutRef.current) {
            console.log(`[StageD] 🔄 Clearing existing auto-save timeout`);
            clearTimeout(autoSaveTimeoutRef.current);
        }
        
        autoSaveTimeoutRef.current = setTimeout(() => {
            console.log(`[StageD] ⏱️ Auto-save timeout triggered, calling autoSave()`);
            autoSave();
        }, 1000); // 1 second debounce
        console.log(`[StageD] ✅ Auto-save scheduled for 1 seconds...`);
    }, [autoSave, projectId]);

    // Load existing edits on mount
    useEffect(() => {
        const loadExistingEdits = async () => {
            if (!projectId) return;

            try {
                console.log(`[StageD] 📥 Loading existing edits...`);
                // Load existing Stage D edits
                const response = await requestWithBackoff(`/api/projects/${projectId}/stage-d-edits`);

                if (response.ok) {
                    const data = await response.json();
                    console.log(`[StageD] 📦 Loaded edits response:`, data);
                    if (data.fieldUpdates) {
                        setFieldUpdates(data.fieldUpdates);
                        // Initialize the last saved state to prevent immediate auto-save
                        lastSavedDataRef.current = JSON.stringify(data.fieldUpdates);
                        console.log(`[StageD] ✅ Loaded existing edits:`, data.fieldUpdates);
                    }
                } else if (response.status !== 404) {
                    console.error('[StageD] Failed to load existing edits:', response.status);
                }
            } catch (error) {
                console.error('[StageD] Error loading existing edits:', error);
            }
        };

        loadExistingEdits();
    }, [projectId]);

    // Auto-save effect when fieldUpdates change
    useEffect(() => {
        console.log(`[StageD] 🔄 fieldUpdates changed, current value:`, fieldUpdates);
        
        // Only schedule auto-save if we have actual changes
        const hasChanges = Object.values(fieldUpdates).some(fieldData => 
            Object.keys(fieldData).length > 0
        );
        
        console.log(`[StageD] 🔍 Change detection:`, {
            hasChanges,
            termChanges: Object.keys(fieldUpdates.term || {}).length,
            contextChanges: Object.keys(fieldUpdates.context || {}).length,
            pageChanges: Object.keys(fieldUpdates.page || {}).length
        });
        
        if (hasChanges) {
            console.log(`[StageD] ✅ Changes detected, scheduling auto-save`);
            scheduleAutoSave();
        } else {
            console.log(`[StageD] ❌ No changes detected, not scheduling auto-save`);
        }
    }, [fieldUpdates, scheduleAutoSave]);

    // Cleanup auto-save timeout and abort controller on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Force auto-save when page becomes hidden to prevent browser throttling issues
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && autoSaveTimeoutRef.current) {
                console.log(`[StageD] 👁️ Page hidden, forcing immediate auto-save to prevent throttling`);
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
                autoSave();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [autoSave]);

    // Force auto-save before page unload 
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (autoSaveTimeoutRef.current) {
                console.log(`[StageD] 🚪 Page unloading, forcing immediate auto-save`);
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = null;
                // Note: async operations have limited time in beforeunload, but we try anyway
                autoSave();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [autoSave]);

    // Create table data sorted by term, then page
    const tableData = useMemo(() => {
        if (!reviewData.termsWithContext || !Array.isArray(reviewData.termsWithContext)) {
            console.log('[StageD] ⚠️ No termsWithContext data available');
            return [];
        }
        
        const data = reviewData.termsWithContext
            // Filter out subentries (entries that have a parent_term)
            .filter(entry => !entry.parent_term || String(entry.parent_term).trim() === '')
            .map(entry => ({
                term: entry.term,
                page: String(entry.page ?? ''),
                context_condensed: entry.context_condensed || '',
                context: entry.context,
                parent_term: entry.parent_term ?? '',
                see_also: entry.see_also || []
            }));

        console.log('[StageD] ✅ Table data ready:', data.length, 'entries (subentries filtered out)');

        // Sort by term, then by page
        return data.sort((a, b) => {
            if (a.term !== b.term) {
                return a.term.localeCompare(b.term);
            }
            return a.page.localeCompare(b.page);
        });
    }, [reviewData.termsWithContext]);

    // Pagination
    const totalPages = Math.ceil(tableData.length / itemsPerPage);
    const startIndex = (currentTablePage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentTableData = tableData.slice(startIndex, endIndex);

    const handleTermClick = (term: string, page: string, row?: any) => {
        setHighlightedTerm(term);
        
        // UNIFIED PAGE CONVERSION: Handle both numeric and string pages
        let backendPage = null;
        if (row && typeof row.page === 'number') {
            // Stage D data: row.page is adjusted page number
            backendPage = row.page;
        } else {
            // Parse displayed page number (remove 'n' suffix if present)
            const displayedPageStr = page.toString().replace(/n$/, '');
            backendPage = parseInt(displayedPageStr, 10) || 1;
        }
        
        // Convert adjusted page number to PDF page number for navigation using pageRange prop
        const pr = pageRange || { startPage: 1 };
        const startPage = pr.startPage || 1;
        const pdfPageNum = backendPage + (startPage - 1);
        
        console.log(`[StageD] 🔍 NAVIGATION DEBUG:`, {
            displayedPage: page,
            backendPage,
            pageRange: pr,
            startPage,
            calculation: `${backendPage} + (${startPage} - 1) = ${pdfPageNum}`,
            rowData: row
        });
        
        setCurrentPage(pdfPageNum);
        console.log(`[StageD] 🎯 FINAL: Navigating to PDF page ${pdfPageNum}`);
    };

    const handleTermEdit = (term: string, page: string, newValue: string) => {
        updateField('term', term, page, newValue);
    };

    const handleContextEdit = (term: string, page: string, newValue: string) => {
        updateField('context', term, page, newValue);
    };

    const handlePageEdit = (term: string, page: string, newValue: string) => {
        updateField('page', term, page, newValue);
    };

    // Tooltip handling functions
    const handleTooltipShow = (event: React.MouseEvent, content: string, type: 'context' | 'crossref') => {
        const rect = event.currentTarget.getBoundingClientRect();
        let x = event.clientX;
        let y = event.clientY;
        
        // Boundary checking to keep tooltip on screen and within left panel
        const tooltipWidth = 320;
        const padding = 16;
        const leftPanel = document.querySelector('.scroll-smooth, .overflow-auto') as HTMLElement | null;
        const leftBounds = leftPanel ? leftPanel.getBoundingClientRect() : { left: 0, right: window.innerWidth } as any;

        if (x + tooltipWidth + padding > leftBounds.right) {
            x = Math.max(leftBounds.left + padding, leftBounds.right - tooltipWidth - padding);
        }
        if (y + 80 > window.innerHeight) {
            y = Math.max(padding, y - 100);
        }
        
        setTooltip({
            show: true,
            content: content,
            x: x,
            y: y,
            type: type
        });
    };

    const handleTooltipHide = () => {
        setTooltip(prev => ({ ...prev, show: false }));
    };

    // Manual save function for backup
    const handleManualSave = useCallback(async () => {
        console.log(`[StageD] Manual save triggered`);
        await autoSave();
    }, [autoSave]);

    const getEditedTerm = (term: string, page: string) => {
        const key = `${term}:${page}`;
        return fieldUpdates.term[key] !== undefined ? fieldUpdates.term[key] : term;
    };

    const getEditedContext = (term: string, page: string, originalContext: string) => {
        const key = `${term}:${page}`;
        return fieldUpdates.context[key] !== undefined ? fieldUpdates.context[key] : (originalContext || '');
    };

    const getEditedPage = (term: string, page: string) => {
        const key = `${term}:${page}`;
        return fieldUpdates.page[key] !== undefined ? fieldUpdates.page[key] : page;
    };

    // Helper functions to determine if a field has been edited AND had an original value (for blue styling)
    const isTermEdited = (term: string, page: string, originalTerm: string) => {
        const key = `${term}:${page}`;
        return fieldUpdates.term[key] !== undefined && 
               fieldUpdates.term[key] !== originalTerm && 
               originalTerm && originalTerm.trim() !== '';
    };

    const isContextEdited = (term: string, page: string, originalContextCondensed: string) => {
        const key = `${term}:${page}`;
        
        // If no user edit exists, this isn't edited
        if (fieldUpdates.context[key] === undefined) {
            return false;
        }
        
        // CRITICAL FIX: Compare against the combined original value (AI + Stage C subentries)
        // This matches what getEditedSubentry would return without user edits
        const originalCombinedValue = (() => {
            const allSubentriesForTerm = new Set<string>();
            
            // 1. AI-generated subentries (from Stage D processing)
            if (originalContextCondensed && originalContextCondensed.trim()) {
                const cleanedContext = originalContextCondensed
                    .replace(/\[C-Stage:\s*([^\]]+)\]/g, '$1')
                    .replace(/\[Legacy:\s*([^\]]+)\]/g, '$1')
                    .replace(/\[AI:\s*([^\]]+)\]/g, '$1')
                    .trim();
                
                const aiSubentries = cleanedContext
                    .split(/[;,|]/)
                    .map(s => s.trim())
                    .filter(s => s.length > 0);
                aiSubentries.forEach(sub => allSubentriesForTerm.add(sub));
            }
            
            // 2. Stage C subentries (terms that became subentries OF this term)
            Object.entries(allSubentries).forEach(([childTerm, parentTerm]) => {
                if (parentTerm === term) {
                    allSubentriesForTerm.add(childTerm);
                }
            });
            
            const finalSubentries = Array.from(allSubentriesForTerm).sort();
            return finalSubentries.join(', ');
        })();
        
        return fieldUpdates.context[key] !== originalCombinedValue;
    };

    const isPageEdited = (term: string, page: string, originalPage: string) => {
        const key = `${term}:${page}`;
        return fieldUpdates.page[key] !== undefined && 
               fieldUpdates.page[key] !== originalPage && 
               originalPage && originalPage.trim() !== '';
    };

    // Helper function to get subentries for a term
    const getSubentriesForTerm = (parentTerm: string): string[] => {
        if (!reviewData.termsWithContext) return [];
        const normalizedParent = (parentTerm || '').toLowerCase().trim();
        
        return reviewData.termsWithContext
            .filter(entry => {
                const p = (entry.parent_term || '').toString().toLowerCase().trim();
                return p.length > 0 && p === normalizedParent;
            })
            .map(entry => entry.term)
            .sort((a, b) => a.localeCompare(b));
    };

    const getRelationshipInfo = (entry: typeof tableData[0]) => {
        const info = [];
        // NOTE: parent_term is no longer shown here - it's now handled by manual subentries in the Subentry column
        
                    // First, check for saved cross-references
            const allCrossRefs = allCrossReferences[entry.term];
            if (allCrossRefs && allCrossRefs.length > 0) {
                info.push(`${allCrossRefs.join(', ')}`);
            }
        
        if (entry.see_also) {
            // Handle see_also safely - it might be a string, array, or other format from CSV
            let seeAlsoArray: string[] = [];
            if (Array.isArray(entry.see_also)) {
                seeAlsoArray = entry.see_also;
            } else if (typeof entry.see_also === 'string' && entry.see_also.trim() !== '') {
                // If it's a string, try to parse it (might be comma-separated or JSON)
                try {
                    // First try to parse as JSON array
                    const parsed = JSON.parse(entry.see_also);
                    if (Array.isArray(parsed)) {
                        seeAlsoArray = parsed;
                    } else {
                        seeAlsoArray = [String(parsed)];
                    }
                } catch {
                    // If JSON parsing fails, split by comma
                    seeAlsoArray = entry.see_also.split(',').map(s => s.trim()).filter(s => s !== '');
                }
            }
            
            if (seeAlsoArray.length > 0) {
                info.push(`See also: ${seeAlsoArray.join(', ')}`);
            }
        }
        return info.length > 0 ? info.join(' | ') : '';
    };

    // Simplified subentry display - shows AI and Stage C subentries without confusing prefixes
    const getEditedSubentry = (term: string, page: string, originalContextCondensed: string | null) => {
        const key = `${term}:${page}`;
        
        // CRITICAL FIX: Check if user has edited this field (including empty string deletions)
        if (fieldUpdates.context[key] !== undefined) {
            return fieldUpdates.context[key]; // Return user edit, even if it's an empty string
        }
        
        // Collect all subentries from different sources
        const allSubentriesForTerm = new Set<string>();
        
        // 1. AI-generated subentries (from Stage D processing)
        if (originalContextCondensed && originalContextCondensed.trim()) {
            // Clean up any residual formatting from old versions
            let cleanedContext = originalContextCondensed
                .replace(/\[C-Stage:\s*([^\]]+)\]/g, '$1')  // Remove [C-Stage: ...] wrapper
                .replace(/\[Legacy:\s*([^\]]+)\]/g, '$1')   // Remove [Legacy: ...] wrapper
                .replace(/\[AI:\s*([^\]]+)\]/g, '$1')       // Remove [AI: ...] wrapper if any
                .trim();
            
            // Split AI subentries by common separators and add each one
            const aiSubentries = cleanedContext
                .split(/[;,|]/)
                .map(s => s.trim())
                .filter(s => s.length > 0);
            aiSubentries.forEach(sub => allSubentriesForTerm.add(sub));
        }
        
        // 2. Stage C subentries (terms that became subentries OF this term)
        Object.entries(allSubentries).forEach(([childTerm, parentTerm]) => {
            if (parentTerm === term) {
                allSubentriesForTerm.add(childTerm);
            }
        });
        
        // Convert to sorted array and return
        const finalSubentries = Array.from(allSubentriesForTerm).sort();
        return finalSubentries.join(', ');
    };

    // Helper function to normalize see_also field to always be an array
    const normalizeSeeAlsoField = (seeAlso: string[] | string | undefined): string[] => {
        if (!seeAlso) return [];
        if (typeof seeAlso === 'string') {
            return seeAlso.split(',').map(s => s.trim()).filter(s => s.length > 0);
        }
        return Array.isArray(seeAlso) ? seeAlso : [];
    };

    // Helper function to apply field updates to the original data
    const getUpdatedTermsWithContext = () => {
        return reviewData.termsWithContext.map(entry => {
            const key = `${entry.term}:${entry.page}`;
            
            // Apply any edits from fieldUpdates
            const updatedEntry = {
                ...entry,
                term: getEditedTerm(entry.term, entry.page),
                context_condensed: getEditedContext(entry.term, entry.page, entry.context_condensed || ''),
                page: getEditedPage(entry.term, entry.page),
                see_also: normalizeSeeAlsoField(entry.see_also)
            };
            
            return updatedEntry;
        });
    };

    const handleExportDocx = async () => {
        try {
            console.log('[StageD] Starting DOCX export...');
            console.log('[StageD] Current fieldUpdates:', fieldUpdates);
            
            // Get the updated data that includes all user edits
            const updatedTermsWithContext = getUpdatedTermsWithContext();
            console.log('[StageD] Updated terms with context (first 3 entries):', updatedTermsWithContext.slice(0, 3));
            
            // Find a specific example to trace
            const exampleEntry = updatedTermsWithContext.find(entry => 
                entry.term.toLowerCase().includes('chips') || 
                entry.context_condensed?.toLowerCase().includes('test')
            );
            if (exampleEntry) {
                console.log('[StageD] Found example entry with CHIPS or test:', exampleEntry);
            }
            
            // Prepare the data to send to the server
            const normalizedData = {
                termsWithContext: updatedTermsWithContext,
                uniqueTerms: reviewData.uniqueTerms,
                workflowId: reviewData.workflowId
            };
            
            console.log('[StageD] Sending updated data with edits applied:', normalizedData);
            console.log('[StageD] Current pageRange being sent:', pageRange);
            
            const { getApiUrl } = await import('../client/lib/config');
            const response = await requestWithBackoff(`/api/projects/${projectId}/export/docx`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('orca_auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    reviewData: normalizedData, 
                    fieldUpdates,
                    pageRange: pageRange // CRITICAL FIX: Pass pageRange from frontend
                })
            });

            console.log('[StageD] DOCX export response status:', response.status);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${filename.replace('.pdf', '')}_index.docx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                console.log('[StageD] DOCX export completed successfully');
            } else {
                const errorText = await response.text();
                console.error('[StageD] DOCX export failed:', response.status, errorText);
                alert(`Failed to export DOCX: ${errorText}`);
            }
        } catch (error) {
            console.error('[StageD] Error exporting DOCX:', error);
            alert(`Error exporting DOCX: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    const handleExportXml = async () => {
        try {
            console.log('[StageD] Starting IXML export...');
            console.log('[StageD] Current fieldUpdates:', fieldUpdates);
            
            // Get the updated data that includes all user edits
            const updatedTermsWithContext = getUpdatedTermsWithContext();
            console.log('[StageD] Updated terms with context (first 3 entries):', updatedTermsWithContext.slice(0, 3));
            
            // Find a specific example to trace
            const exampleEntry = updatedTermsWithContext.find(entry => 
                entry.term.toLowerCase().includes('chips') || 
                entry.context_condensed?.toLowerCase().includes('test')
            );
            if (exampleEntry) {
                console.log('[StageD] Found example entry with CHIPS or test:', exampleEntry);
            }
            
            // Prepare the data to send to the server
            const normalizedData = {
                termsWithContext: updatedTermsWithContext,
                uniqueTerms: reviewData.uniqueTerms,
                workflowId: reviewData.workflowId
            };
            
            console.log('[StageD] Sending updated data with edits applied:', normalizedData);
            
            const { getApiUrl } = await import('../client/lib/config');
            const response = await requestWithBackoff(`/api/projects/${projectId}/export/xml`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('orca_auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reviewData: normalizedData, fieldUpdates })
            });

            console.log('[StageD] IXML export response status:', response.status);

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${filename.replace('.pdf', '')}_index.ixml`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                console.log('[StageD] IXML export completed successfully');
            } else {
                const errorText = await response.text();
                console.error('[StageD] IXML export failed:', response.status, errorText);
                alert(`Failed to export IXML: ${errorText}`);
            }
        } catch (error) {
            console.error('[StageD] Error exporting IXML:', error);
            alert(`Error exporting IXML: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    // Column resizing logic
    const handleMouseDown = (columnType: 'term' | 'page' | 'condensed' | 'full' | 'relationship') => (e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
        
        const startX = e.clientX;
        const startWidth = columnType === 'term' ? termColumnWidth :
                          columnType === 'page' ? pageColumnWidth :
                          columnType === 'condensed' ? condensedContextColumnWidth :
                          columnType === 'full' ? fullContextColumnWidth :
                          relationshipColumnWidth;

        const handleMouseMove = (e: MouseEvent) => {
            const newWidth = Math.max(60, startWidth + (e.clientX - startX));
            
            if (columnType === 'term') setTermColumnWidth(newWidth);
            else if (columnType === 'page') setPageColumnWidth(newWidth);
            else if (columnType === 'condensed') setCondensedContextColumnWidth(newWidth);
            else if (columnType === 'full') setFullContextColumnWidth(newWidth);
            else if (columnType === 'relationship') setRelationshipColumnWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const leftPanel = (
        <div className="h-full flex flex-col bg-background">
            <div className="p-4 border-b border-border/50">
                <h3 className="font-medium text-foreground mb-2">Final Index Review</h3>
                <div className="flex justify-between items-start mb-3">
                    <p className="text-sm text-muted-foreground">
                        Review and edit terms and their condensed contexts. Changes are saved automatically.
                    </p>
                    <div className="text-right">
                        {isSaving && (
                            <p className="text-xs text-blue-400 flex items-center gap-1">
                                <span className="animate-spin">⏳</span>
                                Saving changes...
                </p>
                        )}
                        {!isSaving && !lastSaveTime && Object.keys(fieldUpdates.term).length === 0 && Object.keys(fieldUpdates.context).length === 0 && Object.keys(fieldUpdates.page).length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                No changes yet
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Three separate collapsible sections for relationships */}
            {(Object.keys(allMergedTerms).length > 0 || Object.keys(allCrossReferences).length > 0 || Object.keys(allSubentries).length > 0) && (
                <div className="px-4 border-b border-gray-200">
                    <div className="space-y-2 mb-4">
                        {/* Merged Terms Section */}
                        {Object.keys(allMergedTerms).length > 0 && (
                            <div className="bg-muted/30 border border-border/50 rounded-lg">
                                <button
                                    onClick={() => setIsMergedExpanded(!isMergedExpanded)}
                                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        <h3 className="font-medium text-foreground">Merged Terms</h3>
                                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                                            {Object.keys(allMergedTerms).length}
                                        </span>
                                    </div>
                                    <svg 
                                        className={`w-4 h-4 text-primary transition-transform ${isMergedExpanded ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {isMergedExpanded && (
                                    <div className="px-4 pb-4 border-t border-border/30">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                                            {(() => {
                                                // Group merged terms by master term
                                                const groupedByMaster: { [master: string]: string[] } = {};
                                                Object.entries(allMergedTerms).forEach(([merged, master]) => {
                                                    if (!groupedByMaster[master]) {
                                                        groupedByMaster[master] = [];
                                                    }
                                                    groupedByMaster[master].push(merged);
                                                });
                                                
                                                // Sort master terms and get paginated entries
                                                const sortedMasters = Object.entries(groupedByMaster).sort((a, b) => a[0].localeCompare(b[0]));
                                                return getPaginatedItems(sortedMasters, mergedPage).map(([master, mergedList]) => (
                                                    <div key={master} className="text-sm bg-card rounded p-3 border border-border/30">
                                                        <div className="font-semibold text-foreground">{master}</div>
                                                        <div className="text-xs text-muted-foreground">merged terms:</div>
                                                        <ul className="mt-1 text-muted-foreground list-disc list-inside space-y-0.5">
                                                            {mergedList.map((merged: string) => (
                                                                <li key={merged}>{merged}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                        {(() => {
                                            // Calculate pagination based on grouped master terms
                                            const groupedByMaster: { [master: string]: string[] } = {};
                                            Object.entries(allMergedTerms).forEach(([merged, master]) => {
                                                if (!groupedByMaster[master]) {
                                                    groupedByMaster[master] = [];
                                                }
                                                groupedByMaster[master].push(merged);
                                            });
                                            return renderPaginationControls(
                                                mergedPage, 
                                                getTotalPages(Object.keys(groupedByMaster).length), 
                                                setMergedPage
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Subentries Section */}
                        {Object.keys(allSubentries).length > 0 && (
                            <div className="bg-muted/30 border border-border/50 rounded-lg">
                                <button
                                    onClick={() => setIsSubentriesExpanded(!isSubentriesExpanded)}
                                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                        </svg>
                                        <h3 className="font-medium text-foreground">Subentries</h3>
                                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                                            {Object.keys(allSubentries).length}
                                        </span>
                                    </div>
                                    <svg 
                                        className={`w-4 h-4 text-primary transition-transform ${isSubentriesExpanded ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {isSubentriesExpanded && (
                                    <div className="px-4 pb-4 border-t border-border/30">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                                            {(() => {
                                                // Group subentries by parent term
                                                const groupedByParent: { [parent: string]: string[] } = {};
                                                Object.entries(allSubentries).forEach(([subentry, parent]) => {
                                                    if (!groupedByParent[parent]) {
                                                        groupedByParent[parent] = [];
                                                    }
                                                    groupedByParent[parent].push(subentry);
                                                });
                                                
                                                // Sort parent terms and get paginated entries
                                                const sortedParents = Object.entries(groupedByParent).sort((a, b) => a[0].localeCompare(b[0]));
                                                return getPaginatedItems(sortedParents, subentriesPage).map(([parent, subentryList]) => (
                                                    <div key={parent} className="text-sm bg-card rounded p-3 border border-border/30">
                                                        <div className="font-semibold text-foreground">{parent}</div>
                                                        <div className="text-xs text-muted-foreground">subentries:</div>
                                                        <ul className="mt-1 text-muted-foreground list-disc list-inside space-y-0.5">
                                                            {subentryList.map((subentry: string) => (
                                                                <li key={subentry}>{subentry}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                        {(() => {
                                            // Calculate pagination based on grouped parent terms
                                            const groupedByParent: { [parent: string]: string[] } = {};
                                            Object.entries(allSubentries).forEach(([subentry, parent]) => {
                                                if (!groupedByParent[parent]) {
                                                    groupedByParent[parent] = [];
                                                }
                                                groupedByParent[parent].push(subentry);
                                            });
                                            return renderPaginationControls(
                                                subentriesPage, 
                                                getTotalPages(Object.keys(groupedByParent).length), 
                                                setSubentriesPage
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cross-References Section */}
                        {Object.keys(allCrossReferences).length > 0 && (
                            <div className="bg-muted/30 border border-border/50 rounded-lg">
                                <button
                                    onClick={() => setIsCrossRefsExpanded(!isCrossRefsExpanded)}
                                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-muted/50 transition-colors rounded-t-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        <h3 className="font-medium text-foreground">Cross-References</h3>
                                        <span className="text-sm text-foreground bg-muted/70 px-2 py-1 rounded">
                                            {Object.keys(allCrossReferences).length}
                                        </span>
                                    </div>
                                    <svg 
                                        className={`w-4 h-4 text-primary transition-transform ${isCrossRefsExpanded ? 'rotate-180' : ''}`}
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {isCrossRefsExpanded && (
                                    <div className="px-4 pb-4 border-t border-border/30">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                                            {getPaginatedItems(Object.entries(allCrossReferences), crossRefsPage).map(([term, refs]) => (
                                                <div key={term} className="text-sm bg-card rounded p-3 border border-border/30 shadow-sm">
                                                    <span className="font-medium text-foreground">{term}</span>
                                                    <span className="text-muted-foreground mx-2">↔</span>
                                                    <span className="text-muted-foreground">{refs.join(', ')}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {renderPaginationControls(
                                            crossRefsPage, 
                                            getTotalPages(Object.keys(allCrossReferences).length), 
                                            setCrossRefsPage
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Table Header - matching Stage B/C style */}
            <div className="px-4 pb-3">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                        <div className="w-4 h-4 bg-primary rounded-full"></div>
                    </div>
                    <div>
                        <h3 className="text-foreground text-xl font-semibold">
                            Review Final Index
                        </h3>
                        <div className="text-sm text-muted-foreground mt-1">
                            {(() => {
                                // Count only rows that actually appear in the table dataset
                                const updated = getUpdatedTermsWithContext();
                                return `${updated.length} rows in table`;
                            })()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 px-6 py-4">
                <div className="rounded">
                    <div 
                        className="scroll-smooth" 
                        style={{ 
                            height: 'calc(100vh - 320px)', 
                            minHeight: '540px', 
                            maxHeight: '70vh',
                            overflowY: 'auto',
                            overflowX: 'scroll'
                        }}
                    >
                        <table 
                            className="text-xs w-full border-separate border-spacing-0 border-x-2 border-b-2 border-border rounded-b" 
                            style={{ 
                                tableLayout: 'fixed',
                                boxSizing: 'border-box',
                                fontVariantNumeric: 'tabular-nums',
                                textRendering: 'optimizeSpeed'
                            }}
                        >
                            <thead className="bg-background sticky top-0 z-20 shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 20, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                                <tr className="border-b border-border bg-muted/20">
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border border-t-2 border-t-border bg-background relative"
                                        style={{ 
                                            width: `${termColumnWidth}px`, 
                                            minWidth: `${termColumnWidth}px`, 
                                            maxWidth: `${termColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Term
                                        <div 
                                            className={`absolute top-0 right-[-0.5px] w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
                                            onMouseDown={handleMouseDown('term')}
                                        >
                                        </div>
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border border-t-2 border-t-border bg-background relative"
                                        style={{ 
                                            width: `${pageColumnWidth}px`, 
                                            minWidth: `${pageColumnWidth}px`, 
                                            maxWidth: `${pageColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Page
                                        <div 
                                            className={`absolute top-0 right-[-0.5px] w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
                                            onMouseDown={handleMouseDown('page')}
                                        >
                                        </div>
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border border-t-2 border-t-border bg-background relative"
                                        style={{ 
                                            width: `${condensedContextColumnWidth}px`, 
                                            minWidth: `${condensedContextColumnWidth}px`, 
                                            maxWidth: `${condensedContextColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Subentry
                                        <div 
                                            className={`absolute top-0 right-[-0.5px] w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
                                            onMouseDown={handleMouseDown('condensed')}
                                        >
                                            </div>
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-r border-border border-t-2 border-t-border bg-background relative"
                                        style={{ 
                                            width: `${fullContextColumnWidth}px`, 
                                            minWidth: `${fullContextColumnWidth}px`, 
                                            maxWidth: `${fullContextColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Context
                                        <div 
                                            className={`absolute top-0 right-[-0.5px] w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
                                            onMouseDown={handleMouseDown('full')}
                                        >
                                        </div>
                                    </th>
                                    <th 
                                        className="px-3 py-2 text-left whitespace-nowrap font-medium text-foreground border-t-2 border-t-border bg-muted/20 relative"
                                        style={{ 
                                            width: `${relationshipColumnWidth}px`, 
                                            minWidth: `${relationshipColumnWidth}px`, 
                                            maxWidth: `${relationshipColumnWidth}px`,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Cross-references?
                                        <div 
                                            className={`absolute top-0 right-[-0.5px] w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-400 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
                                            onMouseDown={handleMouseDown('relationship')}
                                        >
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const termToClass = new Map<string, string>();
                                    const palette = ['bg-muted/10','bg-muted/20','bg-muted/30','bg-muted/40'];
                                    let paletteIndex = 0;
                                    return currentTableData.map((item, index) => {
                                        const isLastRow = index === currentTableData.length - 1;
                                        const key = (item.term || '').toLowerCase();
                                        if (!termToClass.has(key)) {
                                            termToClass.set(key, palette[paletteIndex % palette.length]);
                                            paletteIndex++;
                                        }
                                        const rowClass = termToClass.get(key) || '';
                                        return (
                                            <tr key={`${item.term}-${item.page}`} className={`${rowClass} hover:bg-purple-500/10 transition-colors ${isLastRow ? 'border-b-2 border-b-border' : ''}`}>
                                        <td 
                                                className="px-3 py-2 font-medium text-foreground border-r border-border"
                                                style={{ 
                                                    width: `${termColumnWidth}px`, 
                                                    minWidth: `${termColumnWidth}px`, 
                                                    maxWidth: `${termColumnWidth}px`,
                                                    boxSizing: 'border-box'
                                                }}
                                        >
                                            <input
                                                type="text"
                                                value={getEditedTerm(item.term, item.page)}
                                                onChange={(e) => handleTermEdit(item.term, item.page, e.target.value)}
                                                    className={`w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer bg-muted/20 text-foreground ${isTermEdited(item.term, item.page, item.term) ? 'border-blue-500 text-blue-400 font-semibold' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent row click when editing
                                                    handleTermClick(item.term, item.page, item);
                                                }}
                                                        data-term={item.term.toLowerCase()}
                                            />
                                        </td>
                                        <td 
                                            className="px-3 py-2 text-center border-r border-border cursor-pointer"
                                                style={{ 
                                                    width: `${pageColumnWidth}px`, 
                                                    minWidth: `${pageColumnWidth}px`, 
                                                    maxWidth: `${pageColumnWidth}px`,
                                                    boxSizing: 'border-box'
                                                }}
                                                        onClick={() => handleTermClick(item.term, item.page, item)}
                                        >
                                            <input
                                                type="text"
                                                value={formatPageDisplay(getEditedPage(item.term, item.page))}
                                                onChange={(e) => handlePageEdit(item.term, item.page, e.target.value)}
                                                    className={`w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-muted/20 text-foreground ${isPageEdited(item.term, item.page, item.page) ? 'border-blue-500 text-blue-400 font-semibold' : ''}`}
                                                onClick={(e) => e.stopPropagation()} // Prevent row click when editing
                                            />
                                                        </td>
                                                        <td 
                                        className="px-3 py-2 border-r border-border"
                                            style={{ 
                                                width: `${condensedContextColumnWidth}px`, 
                                                minWidth: `${condensedContextColumnWidth}px`, 
                                                maxWidth: `${condensedContextColumnWidth}px`,
                                                boxSizing: 'border-box'
                                            }}
                                                        >
                                                            <input
                                                                type="text"
                                                                value={getEditedSubentry(item.term, item.page, item.context_condensed)}
                                                                onChange={(e) => handleContextEdit(item.term, item.page, e.target.value)}
                                                className={`w-full px-2 py-1 text-xs border border-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-muted/20 text-foreground ${isContextEdited(item.term, item.page, item.context_condensed || '') ? 'border-blue-500 text-blue-400 font-semibold' : ''}`}
                                            placeholder="Enter subentry..."
                                            onClick={(e) => e.stopPropagation()} // Prevent row click when editing
                                                        />
                                                        </td>
                                        <td 
                                            className="px-3 py-2 text-xs text-muted-foreground border-r border-border cursor-pointer"
                                                style={{ 
                                                    width: `${fullContextColumnWidth}px`, 
                                                    minWidth: `${fullContextColumnWidth}px`, 
                                                    maxWidth: `${fullContextColumnWidth}px`,
                                                    boxSizing: 'border-box'
                                                }}
                                            onClick={() => handleTermClick(item.term, item.page, item)}
                                            onMouseEnter={(e) => handleTooltipShow(e, item.context, 'context')}
                                            onMouseLeave={handleTooltipHide}
                                        >
                                            <div className="truncate">
                                                {item.context}
                                            </div>
                                        </td>
                                        <td 
                                                className="px-3 py-2 text-xs text-muted-foreground cursor-pointer"
                                                style={{ 
                                                    width: `${relationshipColumnWidth}px`, 
                                                    minWidth: `${relationshipColumnWidth}px`, 
                                                    maxWidth: `${relationshipColumnWidth}px`,
                                                    boxSizing: 'border-box' 
                                                }}
                                            onClick={() => handleTermClick(item.term, item.page, item)}
                                            onMouseEnter={(e) => handleTooltipShow(e, getRelationshipInfo(item), 'crossref')}
                                            onMouseLeave={handleTooltipHide}
                                        >
                                                <div className="truncate">
                                            {getRelationshipInfo(item)}
                                                </div>
                                                        </td>
                                                    </tr>
                                        );
                                    });
                                })()}
                                            </tbody>
                                        </table>
                            </div>
                    </div>
                </div>

            {/* Pagination Controls */}
            <div className="p-4 border-t border-border/50">
                <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                        Showing {startIndex + 1}-{Math.min(endIndex, reviewData.termsWithContext.length)} of {reviewData.termsWithContext.length} entries
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentTablePage(Math.max(1, currentTablePage - 1))}
                            disabled={currentTablePage === 1}
                        >
                            Previous
                        </Button>
                        <span className="px-2 py-1 text-xs text-muted-foreground">
                            Page {currentTablePage} of {totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentTablePage(Math.min(totalPages, currentTablePage + 1))}
                            disabled={currentTablePage === totalPages}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>

            {/* Export Buttons */}
            <div className="p-4 border-t border-border/50">
                <div className="flex gap-2">
                    <Button
                        onClick={handleExportDocx}
                        variant="outline"
                        className="flex-1"
                        disabled={isSubmitting}
                    >
                        Export as .docx
                    </Button>
                            <Button
                        onClick={handleExportXml}
                        variant="outline"
                        className="flex-1"
                                disabled={isSubmitting}
                            >
                        Export as .ixml (Cindex compatible)
                            </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                    Changes are saved automatically • Export once you're satisfied with the index
                </p>
                        </div>
            
            {/* Enhanced Tooltip */}
            {tooltip.show && (
                <div
                    className="fixed z-50 px-2 py-1 text-xs bg-background/90 border border-border rounded shadow pointer-events-none text-foreground"
                    style={{
                        left: `${tooltip.x + 10}px`,
                        top: `${tooltip.y + 10}px`,
                        maxWidth: '320px'
                    }}
                >
                    {tooltip.content || ''}
                </div>
            )}
                    </div>
    );

    const rightPanel = (
                    <AdvancedPDFViewer
                        projectId={projectId}
                        filename={filename}
                        highlightedTerm={highlightedTerm}
                        currentPage={currentPage}
                        title="Document Display"
                        onClearHighlight={() => setHighlightedTerm('')}
            termUpdates={{}}
            isMinimized={isPdfMinimized}
            onToggleMinimize={() => setIsPdfMinimized(!isPdfMinimized)}
                    />
    );

    return (
        <ResizablePanels
            leftPanel={leftPanel}
            rightPanel={rightPanel}
            defaultLeftWidth={65}
            isRightPanelMinimized={isPdfMinimized}
            onToggleRightPanel={() => setIsPdfMinimized(!isPdfMinimized)}
            />
    );
}; 